import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getHubPreferences } from '../services/api';

interface PreferencesStore {
  // UI Preferences (stored locally)
  showDisconnectedDevices: boolean;
  showHubLog: boolean;
  showRaspiLog: boolean;
  logFrameExpanded: boolean;
  autoRefreshEnabled: boolean;
  refreshInterval: number;
  theme: 'light' | 'dark' | 'auto';
  language: string;

  // Hub Configuration (from backend) - Operational Settings Only
  SOFTWARE_VERSION: string | null;
  VERSION_HUB: string | null;
  
  // MQTT Configuration
  MQTT_BROKER: string | null;
  MQTT_PORT: number | null;
  MQTT_USERNAME: string | null;
  MQTT_PASSWORD: string | null;
  MQTT_KEEPALIVE: number | null;
  MQTT_RECONNECT_DELAY: number | null;
  MQTT_MAX_RECONNECT_ATTEMPTS: number | null;
  
  // Hardware Management
  HARDWARE_DISCOVERY_INTERVAL: number | null;
  HARDWARE_HEARTBEAT_TIMEOUT: number | null;
  
  // System Monitoring
  HEALTH_CHECK_INTERVAL: number | null;
  SYSTEM_MONITORING_INTERVAL: number | null;
  
  // Logging Settings
  LOG_INFO: boolean | null;
  LOG_DEBUG: boolean | null;
  
  // Data Storage
  STORE_PATH: string | null;
  
  // Security Settings
  CORS_ORIGINS: string[] | null;
  JWT_SECRET: string | null;
  
  // RASPI Management
  LOCAL_RASPI_SOFTWARE_FOLDER: string | null;
  RASPI_SOFTWARE_FOLDER: string | null;
  RASPI_DATA_FOLDER: string | null;
  RASPI_SSH_USER: string | null;
  RASPI_SSH_KEY_PATH: string | null;
  RASPI_SSH_PORT: number | null;
  RASPI_SOFTWARE_RESET: string | null;
  RASPI_DEVICE_REBOOT: string | null;
  
  // Camera Settings
  SAVE_CAM_STREAM: boolean | null;
  
  // Arduino Settings
  ARDUINO_AVAILABLE_SOFTWARE_VERSION: string | null;

  // Store Management
  isLoading: boolean;
  error: string | null;

  // Actions
  updatePreference: (key: string, value: any) => void;
  resetToDefaults: () => void;
  clearError: () => void;
  resetStore: () => void;
  loadPreferencesFromHub: () => Promise<void>;
}

/**
 * Preferences Store Defaults
 *
 * UI Preferences (frontend-only):
 *   - showDisconnectedDevices: Show disconnected devices in UI (default: true)
 *   - showHubLog: Show hub log frame (default: true)
 *   - showRaspiLog: Show raspi log frame (default: true)
 *   - logFrameExpanded: Log frame expanded by default (default: false)
 *   - autoRefreshEnabled: Enable auto-refresh (default: true)
 *   - refreshInterval: Auto-refresh interval in seconds (default: 30)
 *   - theme: UI theme (default: 'auto')
 *   - language: UI language (default: 'en')
 *
 * Backend/Hub Preferences (fetched from backend):
 *   - All operational settings loaded from preferences_hub.json
 *   - Network settings are hardcoded in the application
 *
 * To change backend preferences, use the backend API or configuration file.
 * To change UI defaults, edit this file and redeploy the frontend.
 */
export const usePreferencesStore = create<PreferencesStore>()(
  persist(
    (set) => ({
      // Initialize with default values
      showDisconnectedDevices: true,
      showHubLog: false,
      showRaspiLog: false,
      logFrameExpanded: false,
      autoRefreshEnabled: true,
      refreshInterval: 30,
      theme: 'auto',
      language: 'en',

      // Hub Configuration (from backend) - Operational Settings Only
      SOFTWARE_VERSION: null,
      VERSION_HUB: null,
      
      // MQTT Configuration
      MQTT_BROKER: null,
      MQTT_PORT: null,
      MQTT_USERNAME: null,
      MQTT_PASSWORD: null,
      MQTT_KEEPALIVE: null,
      MQTT_RECONNECT_DELAY: null,
      MQTT_MAX_RECONNECT_ATTEMPTS: null,
      
      // Hardware Management
      HARDWARE_DISCOVERY_INTERVAL: null,
      HARDWARE_HEARTBEAT_TIMEOUT: null,
      
      // System Monitoring
      HEALTH_CHECK_INTERVAL: null,
      SYSTEM_MONITORING_INTERVAL: null,
      
      // Logging Settings
      LOG_INFO: null,
      LOG_DEBUG: null,
      
      // Data Storage
      STORE_PATH: null,
      
      // Security Settings
      CORS_ORIGINS: null,
      JWT_SECRET: null,
      
      // RASPI Management
      LOCAL_RASPI_SOFTWARE_FOLDER: null,
      RASPI_SOFTWARE_FOLDER: null,
      RASPI_DATA_FOLDER: null,
      RASPI_SSH_USER: null,
      RASPI_SSH_KEY_PATH: null,
      RASPI_SSH_PORT: null,
      RASPI_SOFTWARE_RESET: null,
      RASPI_DEVICE_REBOOT: null,
      
      // Camera Settings
      SAVE_CAM_STREAM: null,
      
      // Arduino Settings
      ARDUINO_AVAILABLE_SOFTWARE_VERSION: null,

      // Store Management
      isLoading: false,
      error: null,

      // Actions
      updatePreference: (key: string, value: any) => {
        set((state) => ({
          ...state,
          [key]: value,
        }));
      },

      resetToDefaults: () => {
        set((state) => ({
          ...state,
          // Reset UI preferences to defaults
          showDisconnectedDevices: true,
          showHubLog: false,
          showRaspiLog: false,
          logFrameExpanded: false,
          autoRefreshEnabled: true,
          refreshInterval: 30,
          theme: 'auto',
          language: 'en',
          // Reset hub preferences to null (will be loaded from backend)
          SOFTWARE_VERSION: null,
          VERSION_HUB: null,
          MQTT_BROKER: null,
          MQTT_PORT: null,
          MQTT_USERNAME: null,
          MQTT_PASSWORD: null,
          MQTT_KEEPALIVE: null,
          MQTT_RECONNECT_DELAY: null,
          MQTT_MAX_RECONNECT_ATTEMPTS: null,
          HARDWARE_DISCOVERY_INTERVAL: null,
          HARDWARE_HEARTBEAT_TIMEOUT: null,
          HEALTH_CHECK_INTERVAL: null,
          SYSTEM_MONITORING_INTERVAL: null,
          LOG_INFO: null,
          LOG_DEBUG: null,
          STORE_PATH: null,
          CORS_ORIGINS: null,
          JWT_SECRET: null,
          LOCAL_RASPI_SOFTWARE_FOLDER: null,
          RASPI_SOFTWARE_FOLDER: null,
          RASPI_DATA_FOLDER: null,
          RASPI_SSH_USER: null,
          RASPI_SSH_KEY_PATH: null,
          RASPI_SSH_PORT: null,
          RASPI_SOFTWARE_RESET: null,
          RASPI_DEVICE_REBOOT: null,
          SAVE_CAM_STREAM: null,
          ARDUINO_AVAILABLE_SOFTWARE_VERSION: null,
        }));
      },

      clearError: () => {
        set({ error: null });
      },

      resetStore: () => {
        set({
          showDisconnectedDevices: true,
          showHubLog: false,
          showRaspiLog: false,
          logFrameExpanded: false,
          autoRefreshEnabled: true,
          refreshInterval: 30,
          theme: 'auto',
          language: 'en',
          SOFTWARE_VERSION: null,
          VERSION_HUB: null,
          MQTT_BROKER: null,
          MQTT_PORT: null,
          MQTT_USERNAME: null,
          MQTT_PASSWORD: null,
          MQTT_KEEPALIVE: null,
          MQTT_RECONNECT_DELAY: null,
          MQTT_MAX_RECONNECT_ATTEMPTS: null,
          HARDWARE_DISCOVERY_INTERVAL: null,
          HARDWARE_HEARTBEAT_TIMEOUT: null,
          HEALTH_CHECK_INTERVAL: null,
          SYSTEM_MONITORING_INTERVAL: null,
          LOG_INFO: null,
          LOG_DEBUG: null,
          STORE_PATH: null,
          CORS_ORIGINS: null,
          JWT_SECRET: null,
          LOCAL_RASPI_SOFTWARE_FOLDER: null,
          RASPI_SOFTWARE_FOLDER: null,
          RASPI_DATA_FOLDER: null,
          RASPI_SSH_USER: null,
          RASPI_SSH_KEY_PATH: null,
          RASPI_SSH_PORT: null,
          RASPI_SOFTWARE_RESET: null,
          RASPI_DEVICE_REBOOT: null,
          SAVE_CAM_STREAM: null,
          ARDUINO_AVAILABLE_SOFTWARE_VERSION: null,
          isLoading: false,
          error: null,
        });
      },

      loadPreferencesFromHub: async () => {
        set({ isLoading: true, error: null });
        try {
          const preferences = await getHubPreferences();
          
          // Update all preferences from backend
          set((state) => ({
            ...state,
            ...preferences,
            isLoading: false,
          }));
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load preferences',
          });
        }
      },
    }),
    {
      name: 'iop-preferences',
      partialize: (state) => ({
        // Only persist UI preferences locally
        showDisconnectedDevices: state.showDisconnectedDevices,
        showHubLog: state.showHubLog,
        showRaspiLog: state.showRaspiLog,
        logFrameExpanded: state.logFrameExpanded,
        autoRefreshEnabled: state.autoRefreshEnabled,
        refreshInterval: state.refreshInterval,
        theme: state.theme,
        language: state.language,
      }),
    }
  )
); 